'use client';

import {
    useEffect,
    useState,
    lazy,
    Suspense,
    useCallback,
    useMemo,
} from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { ToastContainer } from 'react-toastify';
import { handleRTLLayout } from 'i18n';
import { UserProvider } from 'contexts/UserContext';
import { Button } from 'components/button';
import { useAtom } from 'jotai';
import { jotaiStore } from 'store';
import { useTranslation } from 'react-i18next';
import { useLoadContentfulTranslations } from 'hooks/useLoadContentfulTranslations';
import { useCheckVersion } from 'hooks/useCheckVersion';
import Spinner from './components/Spinner';
import { setAuthHeader } from './api/ApiConfig';
import { useAuth } from './utilities/auth';
import 'react-image-crop/dist/ReactCrop.css';
import './App.css';

// Lazy load the Router component
const LazyRouter = lazy(() => import('./router'));

dayjs.extend(utc);

export default function App() {
    const {
        isLoading,
        isAuthenticated,
        getAccessTokenSilently,
        getIdTokenClaims,
    } = useAuth();
    const [token, setToken] = useState<string>('');
    useCheckVersion();
    const [accessTokenLoading, setAccessTokenLoading] = useState<boolean>(true);
    const [expire, setExpire] = useState<number>(0);
    const [showExpired, setShowExpired] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null); // Error handling state
    const [showUpdate, setShowUpdate] = useState(false);
    const { t } = useTranslation();
    const checkExpire = useCallback(() => {
        if (expire !== 0) {
            const diff = expire - dayjs.utc().unix();
            if (diff < 0) {
                setShowExpired(true);
            }
        }
    }, [expire]);

    useEffect(() => {
        handleRTLLayout();
    }, []);

    useEffect(() => {
        const a = setInterval(checkExpire, 10000);
        return () => {
            clearInterval(a);
        };
    }, [checkExpire]);

    useEffect(() => {
        if (isAuthenticated && token === '') {
            getIdTokenClaims()
                .then((v) => {
                    setExpire(v?.exp || 0);
                })
                .catch((err) => {
                    setError('Error fetching ID token claims.');
                    console.error(err);
                });

            getAccessTokenSilently()
                .then((accessToken) => {
                    setAuthHeader(accessToken);
                    setAccessTokenLoading(false);
                    setToken(accessToken);
                })
                .catch((err) => {
                    setError('Error fetching access token.');
                    console.error(err);
                    setAccessTokenLoading(false);
                });
        }
    }, [isAuthenticated]);

    const loadingOrErrorUI = useMemo(() => {
        if (isLoading || accessTokenLoading) {
            return (
                <div className="w-screen h-screen dark:bg-gray-900 flex items-center justify-center">
                    <Spinner />
                </div>
            );
        }
        if (error) {
            return (
                <div className="w-screen h-screen dark:bg-gray-900 flex items-center justify-center">
                    <div className="w-1/3 p-3">
                        <span className="text-red-500">{error}</span>
                    </div>
                </div>
            );
        }
        return null;
    }, [isLoading, accessTokenLoading, error]);
    const [theme, setTheme] = useAtom(jotaiStore.theme);

    useEffect(() => {
        document.documentElement.setAttribute(
            'data-theme',
            theme === 'dark' ? 'dark' : 'light'
        );
    }, [theme]);
    const { i18n } = useTranslation();
    const currentLocale = i18n.language || 'en';

    const { isLoading: isContentfulLoading } =
        useLoadContentfulTranslations(currentLocale);
    if (isLoading || isContentfulLoading) {
        return <Spinner />;
    }

    // if ('serviceWorker' in navigator) {
    //     const appVersion = localStorage.getItem('appVersion');
    //     navigator.serviceWorker
    //         .register(`/service-worker.js`)
    //         .then((registration) => {
    //             const notifyUpdate = () => {
    //                 setShowUpdate(true);
    //             };
    //             if (registration.waiting) {
    //                 notifyUpdate();
    //             }
    //             registration.onupdatefound = () => {
    //                 const installingWorker = registration.installing;
    //                 if (installingWorker) {
    //                     installingWorker.onstatechange = () => {
    //                         if (installingWorker.state === 'installed') {
    //                             if (navigator.serviceWorker.controller) {
    //                                 notifyUpdate();
    //                             }
    //                         }
    //                     };
    //                 }
    //             };
    //         })
    //         .catch((e) => {
    //             console.error('Service Worker registration failed:', e);
    //         });
    // }

    return (
        loadingOrErrorUI || (
            <>
                <Suspense fallback={<Spinner />}>
                    <UserProvider>
                        <LazyRouter />
                        <ToastContainer />
                    </UserProvider>
                </Suspense>
                {showExpired && (
                    <div className="fixed flex flex-col top-0 start-0 w-screen h-screen bg-gray-900/80 z-50 px-3 items-center justify-center">
                        <div className="w-full md:w-1/3 rounded-3xl bg-base-200 p-3">
                            <div className="flex flex-col justify-center items-center">
                                <h2 className="mb-3 text-2xl font-bold text-primary-content">
                                    {t('Your session has expired')}
                                </h2>
                                <span className="mb-6 text-center text-primary-content text-opacity-70">
                                    {t(
                                        'Your session has expired. Please log in again to continue accessing the platform.'
                                    )}
                                </span>
                                <Button
                                    onClick={() => {
                                        window.location.href =
                                            window.location.toString();
                                    }}
                                    size="small"
                                >
                                    {t('Re-Login')}
                                    <ArrowPathIcon className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
                {showUpdate && (
                    <div className="fixed flex flex-col top-0 start-0 w-screen h-screen bg-gray-900/80 z-50 px-3 items-center justify-center">
                        <div className="w-full md:w-1/3 rounded-3xl bg-base-200 p-3">
                            <div className="flex flex-col justify-center items-center">
                                <h2 className="mb-3 text-2xl font-bold text-primary-content">
                                    {t('Update Available')}
                                </h2>
                                <span className="mb-6 text-center text-primary-content text-opacity-70">
                                    {t(
                                        'A new update is available. Please refresh your page to load the latest version.'
                                    )}
                                </span>
                                <Button
                                    onClick={() => window.location.reload()}
                                    size="small"
                                >
                                    {t('Refresh')}
                                    <ArrowPathIcon className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </>
        )
    );
}
